import { isDefined } from '@racemap/utilities/functions/validation';
import useEventListener from '@use-it/event-listener';
import { type FC, useCallback, useEffect, useState } from 'react';
import tinycolor from 'tinycolor2';
import { FlagContent, InfoSheetTabs, MapType, useStore } from '../store';

export type HashContent = {
  selected: Array<string> | null;
  selectedTags: Map<string, Array<string>> | null;
  selectedStartNumber: Array<string> | null;
  events: Array<number> | null;
  color: string | null;
  time: number | null;
  mapStyle: string | null;
  listOpen: boolean | null;
  flagContent: FlagContent | null;
  selectedFlagContent: FlagContent | null;
  largeMode: boolean | null;
  infoTabsOpenByDefault: boolean | null;
  infoTabsDefaultTab: InfoSheetTabs | null;
  infoTabsDisabledTabs: Array<InfoSheetTabs> | null;
  showAllFlags: boolean | null;
  hideNonSelected: boolean | null;
  zoom: number | null;
  showProgressOnHeightChart: boolean | null;
};

function parseBoolean(value: string): boolean | null {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

export function parseHash(hash: string): HashContent {
  const output: HashContent = {
    selected: null,
    selectedTags: null,
    selectedStartNumber: null,
    events: null,
    color: null,
    time: null,
    mapStyle: null,
    listOpen: null,
    flagContent: null,
    selectedFlagContent: null,
    showAllFlags: null,
    largeMode: null,
    infoTabsOpenByDefault: null,
    infoTabsDefaultTab: null,
    infoTabsDisabledTabs: null,
    hideNonSelected: null,
    zoom: null,
    showProgressOnHeightChart: null,
  };
  const params = new URLSearchParams(hash.substring(1));
  for (const [key, value] of params.entries()) {
    const keyLower = key.toLowerCase();
    if (keyLower === 'selected'.toLowerCase()) {
      output.selected = value.split(',').filter((a) => a !== '');
    }
    if (keyLower === 'selectedStartNumber'.toLowerCase()) {
      output.selectedStartNumber = value.split(',').filter((a) => a !== '');
    }
    if (keyLower === 'filter'.toLowerCase()) {
      const selectedTags: Map<string, Array<string>> = new Map();
      for (const pair of value.split(',').filter((a) => a !== '')) {
        const [key, value] = pair.split(':');
        if (selectedTags.has(key)) {
          selectedTags.get(key)?.push(value);
        } else {
          selectedTags.set(key, [value]);
        }
      }
      output.selectedTags = selectedTags;
    }
    if (keyLower === 'events'.toLowerCase()) {
      output.events = value.split(',').map((a) => Number.parseInt(a, 10));
    }
    if (keyLower === 'color'.toLowerCase()) {
      output.color = value;
    }
    if (keyLower === 'time'.toLowerCase()) {
      output.time = Date.parse(value);
    }
    if (keyLower === 'mapStyle'.toLowerCase()) {
      output.mapStyle = value.toUpperCase();
    }
    if (keyLower === 'listOpen'.toLowerCase()) {
      output.listOpen = parseBoolean(value);
    }
    if (keyLower === 'flagContent'.toLowerCase()) {
      const upperValue = value.toUpperCase();
      output.flagContent = Object.keys(FlagContent).includes(upperValue)
        ? (upperValue as FlagContent)
        : null;
    }
    if (keyLower === 'selectedFlagContent'.toLowerCase()) {
      const upperValue = value.toUpperCase();
      output.selectedFlagContent = Object.keys(FlagContent).includes(upperValue)
        ? (upperValue as FlagContent)
        : null;
    }
    if (keyLower === 'showAllFlags'.toLowerCase()) {
      output.showAllFlags = parseBoolean(value);
    }
    if (keyLower === 'largeMode'.toLowerCase()) {
      output.largeMode = parseBoolean(value);
    }
    if (keyLower === 'hideNonSelected'.toLowerCase()) {
      output.hideNonSelected = parseBoolean(value);
    }
    if (keyLower === 'infoTabs.openByDefault'.toLowerCase()) {
      output.infoTabsOpenByDefault = parseBoolean(value);
    }
    if (keyLower === 'infoTabs.defaultTab'.toLowerCase()) {
      const upperValue = value.toUpperCase();
      output.infoTabsDefaultTab = Object.keys(InfoSheetTabs).includes(upperValue)
        ? (upperValue as InfoSheetTabs)
        : null;
    }
    if (keyLower === 'infoTabs.disabledTabs'.toLowerCase()) {
      output.infoTabsDisabledTabs = value
        .toUpperCase()
        .split(',')
        .map((tab) => (Object.keys(InfoSheetTabs).includes(tab) ? (tab as InfoSheetTabs) : null))
        .filter(isDefined);
    }
    if (keyLower === 'zoom'.toLowerCase()) {
      output.zoom = Number.parseInt(value, 10);
    }
    if (keyLower === 'showProgressOnHeightChart'.toLowerCase()) {
      output.showProgressOnHeightChart = parseBoolean(value);
    }
  }
  return output;
}

function setBaseColor(color: string) {
  const colorObj = tinycolor(color);
  document.documentElement.style.setProperty('--base-color', colorObj.toRgbString());
}

export const HashManager: FC = () => {
  const setSelected = useStore((state) => state.actions.setSelected);
  const setSelectedStartNumber = useStore((state) => state.actions.setSelectedStartNumber);
  const setSelectedTags = useStore((state) => state.actions.setSelectedTags);
  const switchEvent = useStore((state) => state.actions.switchEvent);
  const setTimerTime = useStore((state) => state.actions.setTimerTime);
  const setShowProgressOnHeightChart = useStore(
    (state) => state.actions.setShowProgressOnHeightChart,
  );
  const selected = useStore((state) => state.timer.selected);
  const selectedTags = useStore((state) => state.timer.selectedTags);
  const eventCount = useStore((state) => state.events.length);
  const currentEventIndices = useStore((state) => state.currentEventIndices);
  const setMapStyle = useStore((state) => state.actions.setMapType);

  const [color, setColor] = useState<string | null>(null);

  const handleHashChange = useCallback(
    function handleHashChange() {
      if (window.location.hash !== '') {
        const hashState = parseHash(window.location.hash);
        if (hashState.mapStyle != null) {
          setMapStyle(MapType[hashState.mapStyle.toUpperCase() as keyof typeof MapType]);
        }
        if (hashState.selected != null) {
          setSelected(hashState.selected);
        }
        if (hashState.selectedStartNumber != null) {
          setSelectedStartNumber(hashState.selectedStartNumber);
        }
        if (hashState.selectedTags != null) {
          setSelectedTags(hashState.selectedTags);
        }
        if (hashState.events != null) {
          switchEvent(hashState.events);
        }
        if (hashState.showProgressOnHeightChart != null) {
          setShowProgressOnHeightChart(hashState.showProgressOnHeightChart);
        }
        if (hashState.color != null) {
          setColor(hashState.color);
        }
        if (hashState.time != null) {
          if (!Number.isNaN(hashState.time)) {
            setTimerTime(hashState.time);
          }
        }
      }
    },
    [setSelected, setSelectedStartNumber, setSelectedTags, switchEvent, setTimerTime, setMapStyle],
  );

  useEventListener('hashchange', handleHashChange, window);
  useEffect(() => {
    handleHashChange();
  }, [handleHashChange]);
  useEffect(() => {
    const hashString = [];
    if (selected.length > 0) {
      hashString.push(`selected=${selected.filter((a) => a != null).join(',')}`);
    }
    if (selectedTags.size > 0) {
      hashString.push(
        `filter=${Array.from(selectedTags, ([key, values]) =>
          values
            .map((value) => `${encodeURIComponent(key)}:${encodeURIComponent(value)}`)
            .join(','),
        ).join(',')}`,
      );
    }
    if (eventCount > 1) {
      hashString.push(`events=${currentEventIndices.join(',')}`);
    }
    if (color != null) {
      hashString.push(`color=${color}`);
    }

    if (hashString.length > 0) {
      window.history.replaceState(null, '', `${window.location.pathname}#${hashString.join('&')}`);
    } else {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [selected, selectedTags, color, currentEventIndices, eventCount]);

  useEffect(() => {
    try {
      if (color != null) {
        setBaseColor(`#${color}`);
      }
    } catch (err) {
      console.error(err);
    }
  }, [color]);

  return null;
};
